import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IAdditionalVehicles,
  ILotRateList,
  IParkerProfileDetail,
  IParkingAgreementFetchDetail,
  IStallTypes,
} from 'src/app/shared/interfaces';
import { ParkersV2EditComponent } from '../parkers-v2-edit/parkers-v2-edit.component';
import { MatButtonModule } from '@angular/material/button';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DataService } from 'src/app/core/services/data/data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';

@Component({
  selector: 'app-parkers-v2-detail',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    DatePipe,
    NgIf,
    ParkersV2EditComponent,
    MatButtonModule,
    NgIf,
    ClipboardModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './parkers-v2-detail.component.html',
  styleUrl: './parkers-v2-detail.component.scss',
})
export class ParkersV2DetailComponent implements OnInit {
  status_agreementNotSentOrExempt = -1;
  status_agreementEmailSent: number = 0;
  status_agreementExempt: number = 1;
  status_agreementRemovedFromExemptAndEmailSent: number = 2;
  status_agreementExpired: number = 3;
  status_agreementParkerTermed: number = 4;
  status_agreementViewedButNotSigned: number = 5;
  status_agreementSigned: number = 6;
  status_agreementResent: number = 7;
  status_agreementNotificationFailed: number = 8;
  status_agreementExemptRemoved: number = 9;
  parkerNameLastFirstForCopy: string = '';
  parkerDetail: IParkerProfileDetail;

  @Input() set parkerDetailInput(value: IParkerProfileDetail) {
    this.parkerDetail = value;
    this._parkerService.currentParkerId.set(this.parkerDetail.parkerId);
    this.getSpaceUserIdbyParkerId(this.parkerDetail.parkerId);
  }
  isParkerEditMode: boolean = false;
  stallDescriptions: IStallTypes[] = [];
  marketRateList: ILotRateList[] = [];
  parkingAgreementStatus: number = -1;
  currentParkingAgreement: IParkingAgreementFetchDetail;
  addressForCopy: string = 'address';
  vehicleForCopy: string = 'vehicle';
  spaceUserId: number | undefined = undefined;
  vehicleData: IAdditionalVehicles = {} as IAdditionalVehicles;

  displayEndDate: string;

  //Services
  snackBarService = inject(MatSnackBar);
  dataService = inject(DataService);
  destroyRefService = inject(DestroyRef);
  _parkerService = inject(ParkerStateService);

  constructor() {}

  ngOnInit(): void {
    this.getMarketRateListByLotNumber(this.parkerDetail.lotNumber);
    this.displayEndDate = this.FormatEndDate(this.parkerDetail.endDate);
    this.getParkingAgreementDataForParker(this.parkerDetail.parkerId);
    this.getPrimaryVehicleForParkerId(this.parkerDetail.parkerId);

    this.parkerNameLastFirstForCopy = `${this.parkerDetail.lastName}, ${this.parkerDetail.firstName}`;
    this.onFormatAddressForCopy();
  }

  isParkerEligibleForEdit(): boolean {
    return !this._parkerService.isEditModeEnabled;
  }

  onFormatAddressForCopy(): void {
    if (
      this.parkerDetail.address != null ||
      this.parkerDetail.city != null ||
      this.parkerDetail.state != null ||
      this.parkerDetail.zipCode != null
    ) {
      if (this.parkerDetail.address != null) {
        this.addressForCopy = this.parkerDetail.address;
      }
      if (this.parkerDetail.city != null) {
        if (this.addressForCopy != '') {
          this.addressForCopy += '\n';
        }
        this.addressForCopy += this.parkerDetail.city;
      }

      if (this.parkerDetail.state != null) {
        this.addressForCopy += '  ' + this.parkerDetail.state;
      }

      if (this.parkerDetail.zipCode != null) {
        this.addressForCopy += '  ' + this.parkerDetail.zipCode;
      }
    } else {
      this.addressForCopy = '';
    }
  }

  onPerformParkerEdit(): void {
    this.isParkerEditMode = true;
    this._parkerService.currentParkerIdInEditMode = this.parkerDetail.parkerId;
    this._parkerService.isEditModeEnabled = true;
  }

  OnEditChangesCompleted(newParkerDetail: IParkerProfileDetail): void {
    this.parkerDetail = newParkerDetail;
    this.displayEndDate = this.FormatEndDate(this.parkerDetail.endDate);
    this.isParkerEditMode = false;
  }

  FormatDate(dateValue: Date): string {
    let datePipe: DatePipe = new DatePipe('en-US');
    const dateDisplay: string = datePipe.transform(dateValue, 'M/dd/yy');

    if (dateDisplay === '01/01/0001') return '';
    else return dateDisplay;
  }

  FormatEndDate(dateValue: Date): string {
    let datePipe: DatePipe = new DatePipe('en-US');
    const dateDisplay: string = datePipe.transform(dateValue, 'M/dd/yy');
    if (dateDisplay === '1/01/01') return 'No End Date';
    else return dateDisplay;
  }

  onCopyMessage(message: string): void {
    this.snackBarService.open(message, '', { duration: 3000 });
  }

  setRateDescriptionData(): string {
    let rateNumber: number = 0;
    let rateDescriptionSummary: string = '';

    rateNumber = this.parkerDetail.rateNumber;

    if (rateNumber > 0) {
      let aStallType: IStallTypes;
      let rateData!: ILotRateList;

      aStallType = this.stallDescriptions.find((x) => x.rateNumber == rateNumber)!;

      let ii: number = 0;
      for (ii = 0; ii < this.marketRateList.length; ii++) {
        if (this.marketRateList[ii].rateNumber == rateNumber) {
          rateData = this.marketRateList[ii];
        }
      }

      this.parkerDetail.rateDescription = aStallType.description;
      this.parkerDetail.stallType = aStallType.parkingStallType;
    }

    return rateDescriptionSummary;
  }

  getStallDescriptionListForLotNumber(lotNumber: string) {
    this.dataService.getLotStallDescriptionList(lotNumber).subscribe(
      (res) => {
        this.stallDescriptions = res;
      },
      (err: any) => {},
      () => {
        this.setRateDescriptionData();
      },
    );
  }

  getParkingAgreementDataForParker(parkerId: string): void {
    this.dataService
      .getParkingAgreementDetailByParkerId(parkerId)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {
          this.currentParkingAgreement = results;
        },
        error: (err) => {},
        complete: () => {},
      });
  }

  getMarketRateListByLotNumber(lotNumber: string) {
    this.dataService.getMarketRatesForLotNumber(lotNumber).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {
        this.getStallDescriptionListForLotNumber(lotNumber);
      },
    );
  }

  getSpaceUserIdbyParkerId(parkerId: string) {
    this.dataService
      .getSpaceUserIdbyParkerId(parkerId)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (res) => {
          this.spaceUserId = res;
        },
        error: (err: any) => {
          this.spaceUserId = undefined;
        },
        complete: () => {},
      });
  }

  getPrimaryVehicleForParkerId(parkerId: string): void {
    this.dataService
      .GetPrimaryVehicleForParkerId(parkerId)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (res) => {
          this.vehicleData = res;
        },
        error: (err: any) => {
          this.vehicleData = undefined;
        },
        complete: () => {},
      });
  }
}
